import { chain } from 'ramda';

import {
  IUIUrval,
  IValue,
  IStringConditionObject,
  IChecklistConditionObject,
  NonExtendedConditionObjectType,
  IBooleanConditionObject,
  ITypeTreeConditionObject,
} from '../../../../../../../../../../../../Types/UrvalTypes';
import { CondOperatorType } from '../../../../../../../../../../../../Types/ClientModelTypes';
import { undefinedOrBlank } from '../../../../../../../../../../../../Utils/checkUtils';
import {
  getChecklistValuesAndLabelsWithCode,
  uniqueFromOtherUrval,
} from '../../../../../../../../../../../../Components/Urval/Utils/urvalUtils';
import { clone } from 'ramda';
import {
  IKontodelar,
  IKontodelValue,
} from '../../../../../../../Queries/EkonomiskAnalysKontodelarQuery/EkonomiskAnalysKontodelarQuery';
import { IKontodel } from '../../../../../../../../../../_Common/Components/KonteringManager/Query/KonteringManagerQuery';
import { filterKoddelInSelection } from '../../../../../../../../../../_Common/Utils/filterKodbehorighetUtils';
import { IAttestKodbehorighetUrval } from '../../../../../../../../../../_Common/Components/KonteringManager/KonteringContext';

interface IFlatCondition {
  op: CondOperatorType;
  values: IValue[];
}

export enum UrvalErrorEnum {
  None = 'None',
  SaknarKodbehorighet = 'SaknarKodbehorighet',
  SaknarVarde = 'SaknarVarde',
}

export interface IAttributeErrors {
  name: string;
  values: string[];
  errorType?: UrvalErrorEnum;
}

const nonValueTypes = ['hasvalue', 'all', 'exists', 'notexists', 'isnull'];

export const getConditions = (
  urval: IUIUrval | null
): NonExtendedConditionObjectType[] => {
  if (urval == null) {
    return [];
  }

  let simpleConditions: NonExtendedConditionObjectType[] = [];

  urval.pages.forEach(page => {
    page.conditionObjects.forEach(conditionObject => {
      if (conditionObject.type !== 'Extended') {
        simpleConditions.push(conditionObject);
      }
    });
  });

  return simpleConditions;
};

export const urvalUnchanged = (
  urval: IUIUrval | null,
  originalUrval: IUIUrval | null,
  ignoreHiddenInSimpleUrval?: boolean
): boolean => {
  const urvalValues = getConditions(urval).filter(
    cond => !ignoreHiddenInSimpleUrval || !cond.hideInSimpleUrval
  );
  const compareUrvalValues = getConditions(originalUrval).filter(
    cond => !ignoreHiddenInSimpleUrval || !cond.hideInSimpleUrval
  );
  if (urval && originalUrval) {
    const uniqueOriginal = uniqueFromOtherUrval(originalUrval, urval).filter(
      cond => !ignoreHiddenInSimpleUrval || !cond.hideInSimpleUrval
    );
    const uniqueUpdated = uniqueFromOtherUrval(urval, originalUrval).filter(
      cond => !ignoreHiddenInSimpleUrval || !cond.hideInSimpleUrval
    );
    if (uniqueOriginal.length > 0 || uniqueUpdated.length > 0) {
      return false;
    }
  }
  return urvalValues.every(urvalValue => {
    const matches = compareUrvalValues.filter(
      u => u.attribute.AttributeName === urvalValue.attribute.AttributeName
    );
    switch (urvalValue.type) {
      case 'Checklist':
        return arraytypeUnchanged(
          urvalValue as IChecklistConditionObject,
          matches as IChecklistConditionObject[]
        );
      case 'TypeTree':
        return arraytypeUnchanged(
          urvalValue as ITypeTreeConditionObject,
          matches as ITypeTreeConditionObject[]
        );
      case 'Boolean':
        return booleanUnchanged(
          urvalValue as IBooleanConditionObject,
          matches as IBooleanConditionObject[]
        );
      default:
        return toFromUnchanged(
          urvalValue as IStringConditionObject,
          matches as IStringConditionObject[]
        );
    }
  });
};

const toFromUnchanged = (
  urvalValue: IStringConditionObject,
  matches: IStringConditionObject[]
): boolean => {
  const vValues = toFlatCondition(urvalValue);

  if (matches.length === 0) {
    return vValues[0].values.every(
      value =>
        isUndefinedOrWhitespace(value.value) &&
        isUndefinedOrWhitespace(value.toValue)
    );
  }

  return (
    vValues.every(valueCondition =>
      matches.every(match => {
        const uValues: IFlatCondition[] = match.condition.map(m => ({
          op: m.op,
          values: m.values,
        }));
        return uValues.every(uValue =>
          urvalValuesEqual(valueCondition, uValue)
        );
      })
    ) &&
    matches.every(match => {
      const uValues: IFlatCondition[] = match.condition.map(m => ({
        op: m.op,
        values: m.values,
      }));
      return uValues.every(uVal =>
        vValues.some(vVal => urvalValuesEqual(uVal, vVal))
      );
    })
  );
};

const arraytypeUnchanged = (
  urvalValue: IChecklistConditionObject | ITypeTreeConditionObject,
  matches: Array<IChecklistConditionObject | ITypeTreeConditionObject>
): boolean => {
  if (matches.length === 0) {
    return urvalValue.condition.length === 0;
  }
  if (urvalValue.condition.length === 0) {
    return matches.every(match => match.condition.length === 0);
  }

  return (
    urvalValue.condition.every(value =>
      matches.some(match => match.condition.includes(value))
    ) &&
    matches.every(match =>
      match.condition.every(cond =>
        urvalValue.condition.some(value => value === cond)
      )
    )
  );
};

const booleanUnchanged = (
  urvalValue: IBooleanConditionObject,
  matches: IBooleanConditionObject[]
) => {
  if (matches.length === 0) {
    return !urvalValue.condition.no && !urvalValue.condition.yes;
  }
  return matches.some(
    match =>
      match.condition.no === urvalValue.condition.no &&
      match.condition.yes === urvalValue.condition.yes
  );
};

const urvalValuesEqual = (
  urvalCondition: IFlatCondition,
  compareCondition: IFlatCondition
): boolean => {
  return urvalCondition.values.every(urval =>
    compareCondition.values.some(compareUrval => {
      const valueEqual =
        (urval.value === compareUrval.value &&
          urvalCondition.op === compareCondition.op) ||
        (!isNonValueType(urvalCondition.op) &&
          isUndefinedOrWhitespace(urval.value) &&
          isUndefinedOrWhitespace(compareUrval.value));
      const toValueEqual =
        (urval.toValue === compareUrval.toValue &&
          urvalCondition.op === compareCondition.op) ||
        (!isNonValueType(urvalCondition.op) &&
          isUndefinedOrWhitespace(urval.toValue) &&
          isUndefinedOrWhitespace(compareUrval.toValue));
      return valueEqual && toValueEqual;
    })
  );
};

const valueWithinRange = (val: IValue, compare: IValue): boolean => {
  if (undefinedOrBlank(val.value) || undefinedOrBlank(compare.value)) {
    return true;
  }
  const valToValueValid = undefinedOrBlank(val.toValue);
  const compareToValueValid = undefinedOrBlank(compare.toValue);
  if (!compareToValueValid) {
    const fromLowerValid = String(val.value) >= String(compare.value);

    const fromUpperValid = String(val.value) <= String(compare.toValue);

    const toLowerValid =
      valToValueValid || String(val.toValue) >= String(compare.value);

    const toUpperValid =
      valToValueValid || String(val.toValue) <= String(compare.toValue);
    return fromLowerValid && fromUpperValid && toLowerValid && toUpperValid;
  } else {
    return val.value === compare.value;
  }
};

export const urvalOutsideUrval = (
  inner?: IUIUrval,
  outer?: IUIUrval
): IAttributeErrors[] => {
  if (!inner || !outer) {
    return [];
  }

  const outerConditions = getConditions(outer) as IStringConditionObject[];
  const outerAttributeNames = outerConditions.map(
    x => x.attribute.AttributeName
  );

  const innerConditions = getConditions(inner).filter(x =>
    outerAttributeNames.includes(x.attribute.AttributeName)
  ) as IStringConditionObject[];

  const fail: IAttributeErrors[] = chain(i => {
    return outerConditions.map(o => {
      if (i.attribute.AttributeName !== o.attribute.AttributeName) {
        return { name: i.attribute.AttributeName, values: [] };
      }

      const iValues = i.condition[0].values;
      const oValues = o.condition[0].values;

      const errorValues = iValues.filter(iVal =>
        oValues.every(oVal => {
          return !valueWithinRange(iVal, oVal);
        })
      );
      const errorTexts = errorValues.map(x => {
        const toValue = x.toValue ? `-${x.toValue}` : '';
        return `${x.value}${toValue}`;
      });
      return { name: i.attribute.AttributeName, values: errorTexts };
    });
  }, innerConditions).filter(x => x.values.length > 0);

  return fail;
};

const toFlatCondition = (
  stringCondition: IStringConditionObject
): IFlatCondition[] => {
  return stringCondition.condition.map(u => ({
    op: u.op,
    values:
      u.values.length > 1
        ? u.values.filter(
            value =>
              (value.value && value.value !== '') ||
              (value.toValue && value.toValue !== '')
          )
        : u.values,
  }));
};

const isUndefinedOrWhitespace = (str: string | number | undefined): boolean => {
  return str === undefined || str.toString().match(/^ *$/) !== null;
};

const isNonValueType = (op: string): boolean => {
  return nonValueTypes.includes(op);
};

const getUrvalToFromText = (
  condition: IStringConditionObject
): string | undefined => {
  const values = condition.condition.reduce<string[]>((arr, cond) => {
    cond.values.forEach(x => {
      const toValue = x.toValue ? `-${x.toValue}` : '';
      if (x.value) {
        arr.push(x.value + toValue);
      }
    });
    return arr;
  }, []);
  return values.length > 0
    ? `${condition.attribute.DisplayLabel} [${values.join(', ')}]`
    : undefined;
};

export const NO_URVAL_TEXT = 'Inget urval gjort';

export const getUrvalText = (urval: IUIUrval): string => {
  const urvalValues = getConditions(urval);
  const valueTexts = urvalValues.reduce<string[]>((arr, urvalValue) => {
    switch (urvalValue.type) {
      case 'Checklist': {
        const checklistValue = urvalValue as IChecklistConditionObject;
        if (checklistValue.condition.length > 0) {
          const codes = getChecklistValuesAndLabelsWithCode(
            checklistValue.attribute
          ).reduce<string[]>((arr, x) => {
            if (checklistValue.condition.includes(x.value)) {
              arr.push(x.code || x.displayLabel);
            }
            return arr;
          }, []);
          arr.push(
            `${checklistValue.attribute.DisplayLabel} [${codes.join(', ')}]`
          );
        }
        break;
      }
      case 'TypeTree': {
        const treeValue = urvalValue as ITypeTreeConditionObject;
        if (treeValue.condition.length > 0) {
          arr.push(
            `${treeValue.attribute.DisplayLabel} [${treeValue.condition.join(
              ', '
            )}]`
          );
        }
        break;
      }
      case 'Boolean': {
        const boolValue = urvalValue as IBooleanConditionObject;
        arr.push(
          `${boolValue.attribute.DisplayLabel} [${
            boolValue.condition.yes ? 'Ja' : 'Nej'
          }]`
        );
        break;
      }
      default: {
        const toFromText = getUrvalToFromText(
          urvalValue as IStringConditionObject
        );
        if (toFromText) {
          arr.push(toFromText);
        }
        break;
      }
    }
    return arr;
  }, []);

  return valueTexts.length > 0 ? valueTexts.join(', ') : NO_URVAL_TEXT;
};

export const combineUrval = (
  baseUrval: IUIUrval,
  urval: IUIUrval
): IUIUrval => {
  let newUrval = clone(baseUrval);
  newUrval.pages[0].conditionObjects.push(...urval.pages[0].conditionObjects);
  return newUrval;
};

const koddelValueIsInludedInInterval = (
  koddelValue: IKontodelValue,
  fromValue: string,
  toValue: string
): boolean => {
  const koddelValueKod = koddelValue.id;

  return koddelValueKod >= fromValue && koddelValueKod <= toValue;
};

const kontodelValueIsInludedInUrval = (
  koddelName: string,
  koddelValue: IKontodelValue,
  urval: IUIUrval
): boolean => {
  const conditions = getConditions(urval);
  const conditionsCurrentKoddel = conditions.filter(
    condition => condition.attribute.AttributeName === koddelName
  );

  if (conditionsCurrentKoddel.length === 0) {
    return true;
  } else {
    return conditionsCurrentKoddel.some(condition => {
      if (condition?.type === 'String') {
        if (condition.condition[0].values.every(value => !value.value)) {
          return true;
        } else {
          return condition.condition[0].values.some(value => {
            if (
              value.value &&
              value.toValue &&
              typeof value.value === 'string' &&
              typeof value.toValue === 'string'
            ) {
              const isInludedInInterval = koddelValueIsInludedInInterval(
                koddelValue,
                value.value,
                value.toValue
              );
              return isInludedInInterval;
            } else if (value.value && typeof value.value === 'string') {
              return koddelValue.id === value.value;
            } else {
              return false;
            }
          });
        }
      } else {
        return true;
      }
    });
  }
};

export const kontodelValueIsInludedInAllUrval = (
  koddelName: string,
  koddelValue: IKontodelValue,
  urvalList: IUIUrval[]
): boolean => {
  return urvalList.every(urval =>
    kontodelValueIsInludedInUrval(koddelName, koddelValue, urval)
  );
};
export const kontodelValueIsIncludedInSomeUrval = (
  koddelName: string,
  koddelValue: IKontodelValue,
  urvalList: IUIUrval[]
): boolean => {
  return urvalList.some(urval =>
    kontodelValueIsInludedInUrval(koddelName, koddelValue, urval)
  );
};

export const filterKoddelar = (
  koddelar: IKontodelar,
  urvalList: IUIUrval[]
): IKontodelar => {
  const newKoddelarEntries = Object.entries(koddelar).map(([key, koddel]) => {
    if (koddel) {
      return [
        key,
        {
          ...koddel,
          kontodelar: koddel.kontodelar.filter(koddelValue =>
            kontodelValueIsInludedInAllUrval(key, koddelValue, urvalList)
          ),
        },
      ];
    } else {
      return [key, koddel];
    }
  });

  return Object.fromEntries(newKoddelarEntries);
};

export const isKoddelInUrval = (
  koddel: IKontodel,
  attestUrvalArray: IUIUrval[]
): boolean => {
  let result: boolean = false;
  if (koddel) {
    result = attestUrvalArray.some(attestUrval => {
      return filterKoddelInSelection(koddel, attestUrval);
    });
  }

  return result;
};

export const isKoddelInKodbeh = (
  koddel: IKontodel,
  attestkodbeh: IAttestKodbehorighetUrval[]
): boolean => {
  const urval = attestkodbeh.map(kodbeh => kodbeh.urval);

  return isKoddelInUrval(koddel, urval);
};
